/**
 * Right-pane content for the unified Tests page when a suite is
 * selected (#542).
 *
 * Replaces the "view a suite" half of the old TestSuites.tsx
 * (which presented suites in a list + a side pane showing member
 * test scripts, with edit happening via a modal dialog). The
 * unified design moves suite editing inline:
 *
 *   - **Name + description**: inline-editable header. Save on
 *     blur (matches the immediate-save pattern used by chips and
 *     selectors elsewhere on the page — the operator's mental
 *     model is "field changed → already saved").
 *   - **Members**: ordered list of test cases in the suite,
 *     reorderable via drag (dnd-kit sortable). Each row has a
 *     Remove (×) button + a click target that jumps the sidebar
 *     selection to that test (right pane → TestDetail).
 *     Drag-end fires a full-array PUT — the only mutation surface
 *     the backend exposes today.
 *   - **Add tests**: a search-narrowed picker of tests NOT already
 *     in the suite. Click adds + keeps the picker open so the
 *     operator can batch-add several.
 *   - **Replay templates that use this suite**: read-only chips,
 *     each linking to the Runner page (where templates are
 *     edited). Per the design call in #542, the suite view never
 *     edits templates — keeps the cross-entity binding
 *     unambiguous.
 *   - **Delete suite**: confirm-dialog, removes the suite only
 *     (member test cases stay).
 */
import { useEffect, useMemo, useState, type CSSProperties } from 'react'
import { GripVertical, PlayCircle, Plus, Trash2, X } from 'lucide-react'
import {
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import {
  Badge,
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Textarea,
} from '@/components/ui'
import { SearchInput } from '@/components/SearchInput'
import { healthIcon } from '@/components/TestCaseListItem'
import { useAppContext } from '@/hooks/useAppContext'
import { useManualRunTemplates } from '@/hooks/useManualRunTemplates'
import { ReplayTemplateDialog } from '@/components/ReplayTemplateDialog'
import { useStartRun } from '../useStartRun'
import { testSuitesAPI } from '@/lib/api'
import type { PlanTestCase, ReplayTemplate, TestCase, TestSuite, WorkflowMemberMetadata } from '@/types'

export interface SuiteDetailProps {
  suite: TestSuite
  allCases: PlanTestCase[]
  scriptByCaseId: Record<string, TestCase>
  onReload: () => void | Promise<void>
  onSelectTest: (id: string) => void
  /** Called after the suite is deleted so the parent can clear
   * the selection. */
  onSuiteDeleted: () => void
  /** Open with the name selected, ready to be typed over (#2194). The
   * suite kebab's Rename sets this rather than adding a second rename —
   * the name is already editable here, it just was not reachable without
   * opening the suite and knowing to click it. */
  autoFocusName?: boolean
}

export function SuiteDetail({
  suite,
  allCases,
  scriptByCaseId,
  onReload,
  onSelectTest,
  onSuiteDeleted,
  autoFocusName,
}: SuiteDetailProps) {

  // Header edit state — name/description buffered; save on blur.
  const [draftName, setDraftName] = useState(suite.name)
  const [draftDescription, setDraftDescription] = useState(suite.description ?? '')
  useEffect(() => {
    setDraftName(suite.name)
    setDraftDescription(suite.description ?? '')
  }, [suite.id, suite.name, suite.description])

  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [confirmingDelete, setConfirmingDelete] = useState(false)
  const [addPickerSearch, setAddPickerSearch] = useState('')

  // Members: ordered by the suite's testCaseIds. Missing references
  // (a case was deleted but the suite still holds its id) render
  // as a "missing" placeholder so the operator notices and can
  // clean up.
  const members = useMemo(() => {
    return suite.testCaseIds.map((id) => ({
      id,
      case: allCases.find((c) => c.id === id),
      merged: scriptByCaseId[id],
    }))
  }, [suite.testCaseIds, allCases, scriptByCaseId])

  /** Running a suite is a manual run, so it opens the same replay settings
   * the Runs page does rather than starting straight away (#2302). These
   * knobs — browser, tracing, heal on failure, test data — are in front of
   * the operator at no other moment; the only other way to reach them is to
   * edit the saved template, which changes every future run instead of this
   * one. `replay` is the kind that runs tests that already exist, which is
   * what a suite is. */
  const { selectedVariantId } = useAppContext()
  const { byKind: manualTemplates, reload: reloadRunTemplates } = useManualRunTemplates(selectedVariantId)
  const replayTemplate = manualTemplates.replay
  const startRun = useStartRun()
  const [runDialogOpen, setRunDialogOpen] = useState(false)

  /** Whether running is worth offering at all. A member with no script
   * contributes nothing to a run, so a suite of unwritten tests would start
   * one that executes nothing. The per-test button sets the precedent: it
   * hides and the script pane explains, rather than offering a button that
   * does nothing.
   *
   * This decides whether to OFFER the action, not what runs. What runs is
   * the server's call — it resolves the suite to its current membership at
   * trigger time, scripts or not. */
  const { existingCount, writtenCount } = useMemo(() => {
    // Only members that still resolve to a test. A suite can hold the id of
    // a deleted one, which the list below already renders as missing, and
    // counting those as "unwritten" would explain the wrong problem.
    const existing = members.filter((m) => m.case)
    return {
      existingCount: existing.length,
      writtenCount: existing.filter((m) => m.merged?.scriptCode).length,
    }
  }, [members])
  const canRunSuite = !!replayTemplate && writtenCount > 0
  /** Members that exist, none of them written. A suite whose members have
   * all been deleted is a different problem with its own placeholders, and
   * says nothing here. */
  const noneWritten = existingCount > 0 && writtenCount === 0

  const nonMembers = useMemo(() => {
    const memberSet = new Set(suite.testCaseIds)
    let pool = allCases.filter((c) => !memberSet.has(c.id))
    if (addPickerSearch.trim()) {
      const q = addPickerSearch.toLowerCase()
      pool = pool.filter((c) => c.name.toLowerCase().includes(q))
    }
    return pool
  }, [allCases, suite.testCaseIds, addPickerSearch])

  // ── Mutations ────────────────────────────────────────────────────

  // All four mutations send PARTIAL payloads — only the fields
  // they're actually changing. The prior `...suite` spread carried
  // a snapshot of `name` / `description` / `testCaseIds` taken
  // when the handler was created; if a header save-on-blur and a
  // member edit fired in close succession, the second write would
  // clobber the first's name/description with stale values.
  // Partial-payload writes have no such race surface. PR #543
  // Copilot review fix.
  async function saveHeader() {
    if (draftName === suite.name && (draftDescription || '') === (suite.description || '')) return
    setSaving(true)
    setError(null)
    try {
      await testSuitesAPI.update(suite.id, {
        name: draftName,
        description: draftDescription || undefined,
      })
      await onReload()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to update suite')
    } finally {
      setSaving(false)
    }
  }

  async function reorderMembers(nextIds: string[]) {
    setError(null)
    try {
      await testSuitesAPI.update(suite.id, { testCaseIds: nextIds })
      await onReload()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to reorder')
    }
  }

  async function removeMember(testCaseId: string) {
    setError(null)
    try {
      await testSuitesAPI.update(suite.id, {
        testCaseIds: suite.testCaseIds.filter((id) => id !== testCaseId),
      })
      await onReload()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to remove')
    }
  }

  async function addMember(testCaseId: string) {
    setError(null)
    try {
      await testSuitesAPI.update(suite.id, {
        testCaseIds: [...suite.testCaseIds, testCaseId],
      })
      await onReload()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to add')
    }
  }

  async function toggleCiProtected() {
    setError(null)
    try {
      await testSuitesAPI.update(suite.id, { ciProtected: !suite.ciProtected })
      await onReload()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to update CI protection')
    }
  }

  // #936 PR 1 — workflow-mode helpers. The Convert-to-Workflow /
  // Convert-to-Suite affordance was removed when the unified New
  // Suite dialog landed: Type is set-once at create time. If the
  // operator picks the wrong type, they re-create. (See
  // `NewSuiteDialog` docstring for the rationale.)

  async function toggleSkipRest() {
    setError(null)
    try {
      const next = suite.onMemberFailure === 'skip-rest' ? 'continue' : 'skip-rest'
      await testSuitesAPI.update(suite.id, { onMemberFailure: next })
      await onReload()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to update failure handling')
    }
  }

  async function setStepLabel(testCaseId: string, stepLabel: string) {
    setError(null)
    // Build the next workflowMetadata map from the current one,
    // overlaying the edited entry. Sending the full map (rather than
    // a partial) is consistent with how the rest of this page does
    // PUTs — partial-payload writes for the field being changed, but
    // the field's value is the complete next state.
    const current = (suite.workflowMetadata ?? {}) as Record<string, WorkflowMemberMetadata>
    const next: Record<string, WorkflowMemberMetadata> = { ...current }
    if (stepLabel.trim()) {
      next[testCaseId] = { stepLabel: stepLabel.trim() }
    } else {
      // Empty label = clear the entry; render falls back to the test name.
      delete next[testCaseId]
    }
    try {
      await testSuitesAPI.update(suite.id, { workflowMetadata: next })
      await onReload()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to update step label')
    }
  }

  // CI-protected suites require the user to type the suite name to
  // confirm — defense against an admin accidentally archiving a suite
  // referenced by a customer's CI pipeline. See ADR 0015 § integrity.
  const [confirmName, setConfirmName] = useState('')
  const requiresTypedConfirm = suite.ciProtected
  const canConfirmDelete = !requiresTypedConfirm || confirmName === suite.name

  async function doDelete() {
    setError(null)
    try {
      await testSuitesAPI.delete(
        suite.id,
        requiresTypedConfirm ? { force: true, confirmName: suite.name } : undefined,
      )
      setConfirmingDelete(false)
      setConfirmName('')
      onSuiteDeleted()
      await onReload()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to delete suite')
      setConfirmingDelete(false)
      setConfirmName('')
    }
  }

  // ── DnD wiring ───────────────────────────────────────────────────

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  )

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event
    if (!over || active.id === over.id) return
    const oldIndex = suite.testCaseIds.indexOf(String(active.id))
    const newIndex = suite.testCaseIds.indexOf(String(over.id))
    if (oldIndex < 0 || newIndex < 0) return
    const next = arrayMove(suite.testCaseIds, oldIndex, newIndex)
    void reorderMembers(next)
  }

  // ── Render ───────────────────────────────────────────────────────

  return (
    <div className="h-full flex flex-col bg-card overflow-y-auto">
      {/* Toolbar */}
      <div className="flex-none px-5 py-2 border-b flex items-center gap-2 text-xs">
        {/* #936 PR 1 — pill reflects suite kind. Workflow mode pill
            gets primary styling to make the distinction obvious. */}
        <Badge variant={suite.isWorkflow ? 'default' : 'secondary'} className="text-xs">
          {suite.isWorkflow ? 'Workflow' : 'Suite'}
        </Badge>
        {suite.ciProtected && (
          <Badge
            variant="warning"
            className="text-xs"
            title="This suite is marked as a CI/CD entry point. Deletion requires name-typed confirmation."
          >
            CI
          </Badge>
        )}
        <span className="text-muted-foreground">
          {members.length} {suite.isWorkflow ? 'test' : 'member'}{members.length !== 1 ? 's' : ''}
        </span>
        {saving && <span className="text-muted-foreground italic">saving…</span>}
        <div className="ml-auto flex gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={toggleCiProtected}
            title={
              suite.ciProtected
                ? 'Remove the CI/CD entry-point marker. Deletion will no longer require name-typed confirmation.'
                : 'Mark this suite as a CI/CD entry point. Deletion will require name-typed confirmation to protect against accidental removal of suites referenced by a CI pipeline.'
            }
          >
            {suite.ciProtected ? 'Unmark CI' : 'Mark CI'}
          </Button>
          {canRunSuite && (
            <Button
              size="sm"
              onClick={() => setRunDialogOpen(true)}
              title="Choose how this run behaves, then run this suite."
            >
              <PlayCircle className="h-3 w-3 mr-1" /> Run Suite
            </Button>
          )}
          {noneWritten && (
            <span className="text-xs text-muted-foreground self-center">
              Not written yet
            </span>
          )}
          <Button variant="ghost" size="sm" className="text-destructive h-7" onClick={() => setConfirmingDelete(true)}>
            <Trash2 className="h-3 w-3 mr-1" /> Delete
          </Button>
        </div>
      </div>

      {/* #936 PR 1 — workflow-mode failure-handling toggle. Only shown
          when the suite is in workflow mode; reads cleanly as a setting
          attached to the workflow itself. */}
      {suite.isWorkflow && (
        <div className="flex-none px-5 py-2 border-b text-xs bg-muted/30">
          <label className="flex items-start gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={suite.onMemberFailure === 'skip-rest'}
              onChange={toggleSkipRest}
              className="mt-0.5 accent-primary"
            />
            <div>
              <div className="font-medium">Skip remaining tests if an earlier one fails</div>
              <div className="text-muted-foreground mt-0.5">
                When enabled, OttoTester runs the workflow's tests in order and skips later tests once any test fails — useful for long-running workflows. Off by default; tests run independently in parallel.
              </div>
            </div>
          </label>
        </div>
      )}

      {error && (
        <div className="flex-none border-b bg-destructive/10 text-destructive text-xs px-5 py-2 flex items-start gap-3" role="alert">
          <span className="flex-1"><strong>Error:</strong> {error}</span>
          <button type="button" className="underline" onClick={() => setError(null)}>Dismiss</button>
        </div>
      )}

      {/* Header — inline-editable name + description */}
      <div className="flex-none px-5 py-3 border-b space-y-2">
        <Input
          value={draftName}
          onChange={(e) => setDraftName(e.target.value)}
          onBlur={saveHeader}
          className="text-lg font-semibold"
          aria-label="Suite name"
          autoFocus={autoFocusName}
          /* Select the whole name, not just place a caret: someone who
             chose Rename means to replace it, and having to clear it
             first is a step the menu already implied was done. */
          onFocus={e => { if (autoFocusName) e.currentTarget.select() }}
        />
        <Textarea
          value={draftDescription}
          onChange={(e) => setDraftDescription(e.target.value)}
          onBlur={saveHeader}
          rows={2}
          placeholder="Description (optional)"
          aria-label="Suite description"
          className="text-sm"
        />
        {/* Replay templates that bind this suite */}
      </div>

      {/* Member list with drag-reorder */}
      <div className="flex-1 px-5 py-3 space-y-3">
        <div>
          <Label className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">
            {suite.isWorkflow ? 'Tests' : 'Members'} ({members.length})
          </Label>
          {members.length === 0 ? (
            <p className="text-sm text-muted-foreground italic mt-2">
              No tests yet — add some from the picker below.
            </p>
          ) : (
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
              <SortableContext items={suite.testCaseIds} strategy={verticalListSortingStrategy}>
                <div className="mt-2 space-y-1">
                  {members.map((m, idx) => (
                    <SortableMemberRow
                      key={m.id}
                      id={m.id}
                      index={idx + 1}
                      planCase={m.case}
                      merged={m.merged}
                      onOpen={() => onSelectTest(m.id)}
                      onRemove={() => void removeMember(m.id)}
                      workflowMode={!!suite.isWorkflow}
                      stepLabel={
                        (suite.workflowMetadata as Record<string, WorkflowMemberMetadata> | undefined)?.[m.id]
                          ?.stepLabel ?? ''
                      }
                      onStepLabelChange={(next) => void setStepLabel(m.id, next)}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          )}
        </div>

        {/* Add picker */}
        <div>
          <Label className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">
            Add tests
          </Label>
          <SearchInput
            value={addPickerSearch}
            onChange={setAddPickerSearch}
            placeholder="Search tests not in this suite..."
          />
          <div className="mt-2 border rounded-md max-h-64 overflow-y-auto">
            {nonMembers.length === 0 ? (
              <p className="text-xs text-muted-foreground italic p-3">
                {addPickerSearch ? 'No tests match this search.' : 'Every test in this variant is already in the suite.'}
              </p>
            ) : (
              <div className="divide-y">
                {nonMembers.slice(0, 50).map((c) => (
                  <button
                    key={c.id}
                    onClick={() => void addMember(c.id)}
                    className="w-full text-left px-3 py-2 hover:bg-muted/50 flex items-center gap-2"
                  >
                    <Plus className="h-3 w-3 shrink-0 text-muted-foreground" />
                    <span className="text-sm flex-1 truncate" title={c.name}>{c.name}</span>
                    <span className="text-[10px] text-muted-foreground">{c.priority}</span>
                  </button>
                ))}
                {nonMembers.length > 50 && (
                  <p className="text-[10px] text-muted-foreground italic p-2 text-center">
                    +{nonMembers.length - 50} more — refine search to narrow
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Delete confirmation. ciProtected suites add a name-typed gate
       * so the operator can't accidentally remove a CI-referenced suite. */}
      <Dialog
        open={confirmingDelete}
        onOpenChange={(open) => {
          if (!open) {
            setConfirmingDelete(false)
            setConfirmName('')
          }
        }}
      >
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>
              {requiresTypedConfirm ? 'Delete CI/CD entry-point suite?' : 'Delete suite?'}
            </DialogTitle>
          </DialogHeader>
          <DialogBody className="space-y-3">
            <p className="text-sm text-muted-foreground">
              {requiresTypedConfirm ? (
                <>
                  <strong>"{suite.name}"</strong> is marked as a CI/CD entry point.
                  Deleting it may break any pipeline that references it. The suite
                  will be archived and recoverable from the Archived tab for 30 days
                  before the sweep removes it permanently. To confirm, type the suite
                  name below.
                </>
              ) : (
                <>
                  Archive <strong>"{suite.name}"</strong>? It will be recoverable
                  from the Archived tab for 30 days before the sweep removes it
                  permanently. The member test scripts stay; only the grouping is
                  removed.
                </>
              )}
            </p>
            {requiresTypedConfirm && (
              <Input
                value={confirmName}
                onChange={(e) => setConfirmName(e.target.value)}
                placeholder={suite.name}
                aria-label="Type the suite name to confirm"
                autoFocus
              />
            )}
          </DialogBody>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setConfirmingDelete(false)
                setConfirmName('')
              }}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={doDelete}
              disabled={!canConfirmDelete}
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* The replay settings, opened FOR this suite (#2302). Mounted only
          while open and keyed by the suite, because `presetSuiteId` seeds
          the dialog's state on mount and nothing watches it afterwards —
          without the key, opening this for a second suite would show the
          first one. `launch` runs what is on screen without saving; the
          dialog's own Save is still there for an operator who wants the
          change to stick. */}
      {runDialogOpen && replayTemplate && selectedVariantId && (
        <ReplayTemplateDialog
          key={suite.id}
          template={replayTemplate as unknown as ReplayTemplate}
          variantId={selectedVariantId}
          applicationId={suite.applicationId}
          suites={[suite]}
          presetSuiteId={suite.id}
          schedulable
          onClose={() => setRunDialogOpen(false)}
          onSaved={() => {
            // Reload the RUN SETTINGS, not the suite. Save and run writes
            // the template and then launches with no override, trusting the
            // saved row; without this the next open would reseed from the
            // copy this component still holds and show the pre-save browser,
            // capture, heal and hook values. The Runs page reloads for the
            // same reason (Codex review on #2303).
            void reloadRunTemplates()
            void onReload()
          }}
          launch={(settings) => {
            setRunDialogOpen(false)
            return startRun.start(replayTemplate.id, `Running ${suite.name}…`, settings)
          }}
        />
      )}
    </div>
  )
}

// ── Sortable row ─────────────────────────────────────────────────

function SortableMemberRow({
  id,
  index,
  planCase,
  merged,
  onOpen,
  onRemove,
  workflowMode,
  stepLabel,
  onStepLabelChange,
}: {
  id: string
  index: number
  planCase: PlanTestCase | undefined
  merged: TestCase | undefined
  onOpen: () => void
  onRemove: () => void
  /** #936 PR 1 — when true, render the step-label input in place of
   * the priority badge. Off = today's batch-suite row. */
  workflowMode: boolean
  /** Current label (empty string when unset; UI shows test name as
   * placeholder so the row reads sensibly until the user types). */
  stepLabel: string
  /** Save handler. Fires on blur (matches the suite header's save-
   * on-blur pattern). Empty trimmed value clears the entry. */
  onStepLabelChange: (next: string) => void
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id })
  const style: CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  }
  const { Icon: HealthIcon, cls: healthCls } = healthIcon({
    lastPassedAt: merged?.lastPassedAt,
    lastFailedAt: merged?.lastFailedAt,
  })

  // Local draft so typing isn't intercepted by re-renders before the
  // operator finishes editing. Sync from props when the underlying
  // value changes (e.g., after a save round-trip).
  const [draftLabel, setDraftLabel] = useState(stepLabel)
  useEffect(() => {
    setDraftLabel(stepLabel)
  }, [stepLabel])

  if (!planCase) {
    return (
      <div
        ref={setNodeRef}
        style={style}
        className="flex items-center gap-2 px-2 py-1.5 rounded bg-destructive/5 border border-destructive/20"
      >
        <span className="text-[10px] text-muted-foreground w-6">{index}.</span>
        <span className="flex-1 text-xs font-mono text-destructive italic">
          {id.slice(0, 8)}… (missing — case deleted)
        </span>
        <button onClick={onRemove} className="text-destructive hover:text-destructive/80" aria-label="Remove missing reference">
          <X className="h-3 w-3" />
        </button>
      </div>
    )
  }

  return (
    <div ref={setNodeRef} style={style} className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-muted/40 group">
      <button
        {...attributes}
        {...listeners}
        className="touch-none text-muted-foreground hover:text-foreground cursor-grab active:cursor-grabbing"
        aria-label="Drag to reorder"
      >
        <GripVertical className="h-4 w-4" />
      </button>
      <span className="text-[10px] text-muted-foreground w-6">{index}.</span>
      <HealthIcon className={`h-3.5 w-3.5 shrink-0 ${healthCls}`} />
      <button onClick={onOpen} className="flex-1 text-left text-sm truncate hover:text-primary" title={planCase.name}>
        {planCase.name}
      </button>
      {workflowMode ? (
        <Input
          value={draftLabel}
          onChange={(e) => setDraftLabel(e.target.value)}
          onBlur={() => {
            if (draftLabel !== stepLabel) onStepLabelChange(draftLabel)
          }}
          placeholder="step label"
          aria-label={`Step label for ${planCase.name}`}
          className="h-7 w-32 text-xs"
        />
      ) : (
        <span className="text-[10px] text-muted-foreground">{planCase.priority}</span>
      )}
      <button
        onClick={onRemove}
        className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-destructive"
        aria-label={`Remove ${planCase.name} from suite`}
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  )
}
